import { Card } from 'components/Card';
import { CardRow, CardRowProps } from 'components/CardRow';
import { theme } from 'lib/theme';
import { animateNext } from 'lib/animateNext';
import { without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { eventSelectScreenSelection, NAVIGATE_MODAL } from 'store/actions';
import { ModalScreens } from 'components/Navigation/types';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { ICONS } from 'components/Icon';
import { randomKey } from 'lib/riskbase';

export type OptionFieldOption<T> = {
  internalValue: T;
  name: string;
  value?: string;
  icon?: ICONS;
  iconColor?: string;
  iconSize?: number;
  labelAlignTop?: CardRowProps['labelAlignTop'];
  groupName?: string;
};

type PropsWithOnChange<T> = {
  name?: never;
  onChange: (value: T[]) => void;
  onChangeWithName?: never;
};

type PropsWithOnChangeWithName<K extends string, T> = {
  name: K;
  onChange?: never;
  onChangeWithName: (key: K, value: T[]) => void;
};

type OptionFieldProps<K extends string, T> = (
  | PropsWithOnChange<T>
  | PropsWithOnChangeWithName<K, T>
) & {
  modalLabel: string;
  label?: string;
  labelStyle?: CardRowProps['labelStyle'];
  labelAlignTop?: boolean;
  value: T[];
  options: OptionFieldOption<T>[];
  noValueOption?: Omit<OptionFieldOption<T>, 'internalValue'>;
  multiSelect?: boolean;
  expandedInlineFirst?: boolean;
  expandedInlineLast?: boolean;
  placeholder?: string;
  showNoValueOption?: boolean;
  showBadge?: boolean;
  modalSize?: 'small' | 'fullscreen' | 'large';
  showFieldTypeIcon?: boolean;
  valueNumberOfLines?: CardRowProps['valueNumberOfLines'];
  valueNumberOfLinesWithLabel?: CardRowProps['valueNumberOfLinesWithLabel'];
  modalSearchable?: boolean;
  modalEnableSelectAll?: boolean;
  showLabelInCloseHeader?: boolean;
  showCloseHeaderSingleSelect?: boolean;
  modalText?: string;
  forceModal?: boolean;
  modalCloseOnDone?: boolean;
  modalShowQuickChoose?: boolean;
  disabled?: boolean;
  clearable?: boolean;
};

export const OptionField = <K extends string, T>({
  modalLabel,
  label,
  labelStyle,
  labelAlignTop,
  value,
  valueNumberOfLines,
  valueNumberOfLinesWithLabel,
  options,
  name,
  onChangeWithName,
  multiSelect = false,
  onChange,
  expandedInlineFirst,
  expandedInlineLast,
  showNoValueOption,
  noValueOption,
  showBadge,
  modalSize,
  placeholder,
  showFieldTypeIcon = true,
  modalSearchable,
  modalEnableSelectAll,
  showLabelInCloseHeader,
  showCloseHeaderSingleSelect,
  modalText,
  forceModal: forceModalIncoming,
  modalCloseOnDone,
  modalShowQuickChoose,
  disabled,
  clearable,
}: OptionFieldProps<K, T>): React.ReactElement => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = React.useState(false);
  const forceModal = forceModalIncoming || options.length > 8;

  const selectKeyRef = React.useRef(randomKey());

  const handleShowSelectModal = () => {
    const selectOptions = options.map((option) => ({
      groupName: option.groupName,
      name: option.name,
      value: option.value,
      internalValue: option.internalValue as string,
      labelAlignTop: option.labelAlignTop,
    }));
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        size: modalSize,
        params: {
          text: modalText,
          title: modalLabel,
          searchable: modalSearchable,
          enableSelectAll: modalEnableSelectAll,
          eventKey: selectKeyRef.current,
          multiSelect,
          options: showNoValueOption
            ? [
                {
                  name: noValueOption?.name || 'No Value',
                  value: noValueOption?.value,
                  groupName: noValueOption?.groupName,
                  labelAlignTop: noValueOption?.labelAlignTop,
                  internalValue: undefined,
                },
                ...selectOptions,
              ]
            : selectOptions,
          value: value as unknown as string[],
          closeOnDone: modalCloseOnDone,
          showQuickChoose: modalShowQuickChoose,
        },
      }),
    );
  };

  const handleToggleEdit = () => {
    if (forceModal) {
      handleShowSelectModal();
      return;
    }
    animateNext();
    setIsEditing(!isEditing);
  };

  const handlePress = (newValue: T | undefined) => {
    if (multiSelect) {
      if (name && onChangeWithName) {
        onChangeWithName(
          name,
          newValue === undefined
            ? []
            : value.includes(newValue)
              ? without(value, newValue)
              : [...value, newValue],
        );
      } else if (onChange) {
        onChange(
          newValue === undefined
            ? []
            : value.includes(newValue)
              ? without(value, newValue)
              : [...value, newValue],
        );
      }
    } else {
      if (newValue ? !value.includes(newValue) : value.length) {
        if (name && onChangeWithName) {
          onChangeWithName(name, newValue ? [newValue] : []);
        } else if (onChange) {
          onChange(newValue ? [newValue] : []);
        }
      }
      setIsEditing(false);
    }
  };

  const handleClear = () => {
    if (name && onChangeWithName) {
      onChangeWithName(name, []);
    } else if (onChange) {
      onChange([]);
    }
    setIsEditing(false);
  };

  React.useEffect(() => {
    const eventNumber = doAddEvent(eventSelectScreenSelection, (action) => {
      if (action.payload.key === selectKeyRef.current) {
        if (name && onChangeWithName) {
          onChangeWithName(name, action.payload.stringValues as T[]);
        } else if (onChange) {
          onChange(action.payload.stringValues as T[]);
        }
      }
    });
    return () => {
      doRemoveEvent(eventNumber);
    };
  }, [name, onChange, onChangeWithName]);

  if (!isEditing) {
    const chosenOptions = options.filter((option) =>
      value.includes(option.internalValue),
    );
    const chosenOption = !chosenOptions.length
      ? noValueOption
      : chosenOptions.length === 1
        ? chosenOptions[0]
        : undefined;

    let fieldLabel: string;
    let fieldValue: string | undefined;
    let fieldLabelAlignTop: boolean | undefined;
    let fieldLabelColor: string | undefined;
    if (label) {
      fieldLabel = label;
      fieldValue =
        chosenOptions.map((o) => o.name).join(', ') ||
        placeholder ||
        noValueOption?.name ||
        'No Value';
      fieldLabelAlignTop = labelAlignTop;
    } else {
      fieldLabel =
        chosenOptions.map((o) => o.name).join(', ') ||
        placeholder ||
        noValueOption?.name ||
        'No Value';
      fieldValue = chosenOption?.value;
      fieldLabelAlignTop = chosenOption?.labelAlignTop;
      fieldLabelColor =
        !value.length && !chosenOption?.value
          ? theme.colors.placeholder
          : undefined;
    }

    return (
      <CardRow
        label={fieldLabel}
        labelStyle={labelStyle}
        labelAlignTop={fieldLabelAlignTop}
        labelColor={fieldLabelColor}
        value={fieldValue}
        labelNumberOfLines={0}
        valueNumberOfLines={valueNumberOfLines}
        valueNumberOfLinesWithLabel={valueNumberOfLinesWithLabel}
        disabled={disabled}
        onPress={handleToggleEdit}
        navigationType={
          showFieldTypeIcon
            ? forceModal
              ? 'screen'
              : multiSelect
                ? 'input-check'
                : 'input-radio'
            : undefined
        }
        showBadge={showBadge}
        actionIcon={ICONS.CircleXmarkLight}
        onAction={clearable && chosenOptions.length ? handleClear : undefined}
      />
    );
  }

  // Show expanded field

  const finalOptions: OptionFieldOption<T | ''>[] = multiSelect
    ? options
    : showNoValueOption
      ? [
          {
            name: noValueOption?.name || 'No Value',
            value: noValueOption?.value,
            groupName: noValueOption?.groupName,
            labelAlignTop: noValueOption?.labelAlignTop,
            icon: undefined,
            iconColor: undefined,
            iconSize: undefined,
            internalValue: '',
          },
          ...options,
        ]
      : options;

  return (
    <Card
      marginHorizontal={false}
      expanded
      expandedInline
      expandedInlineFirst={expandedInlineFirst}
      expandedInlineLast={expandedInlineLast}
    >
      {(multiSelect || showCloseHeaderSingleSelect) && (
        <CardRow
          label={`${label && showLabelInCloseHeader ? `${label} • ` : ''}Close`}
          labelSize="xsmall"
          labelColor={theme.colors.blue}
          labelStyle={{ textAlign: 'center' }}
          onPress={handleToggleEdit}
          style={{ minHeight: 0 }}
          contentStyle={{ paddingTop: 6, paddingBottom: 6 }}
        />
      )}
      {finalOptions.map((option) => (
        <CardRow
          key={option.internalValue ? `${option.internalValue}` : 'no-value'}
          label={option.name}
          value={option.value}
          labelAlignTop={option.labelAlignTop}
          radioValue={
            !multiSelect
              ? option.internalValue === ''
                ? !value.length
                : value.includes(option.internalValue)
              : undefined
          }
          checkedValue={
            multiSelect
              ? option.internalValue === ''
                ? !value.length
                : value.includes(option.internalValue)
              : undefined
          }
          icon={option.icon}
          iconColor={option.iconColor}
          iconSize={option.iconSize}
          onPress={() => handlePress(option.internalValue || undefined)}
        />
      ))}
    </Card>
  );
};
